import { getIdentifiedPlatformConfig } from '@hmhco/login-config';

/**
 * Requests an Early Access registration token from the Okta Services Gateway.
 *
 * Sample usage:
 * requestEarlyAccessRegistrationToken({
 *   orgRefId: 'a1b2c3',
 *   pid: '92028248',
 *   orgType: 'DISTRICT',
 *   countryCode: 'US',
 *   email: 'teacher@example.com',
 * });
 *
 * @param {Object} params
 * @param {string} params.orgRefId - Organization reference ID
 * @param {string} params.pid - Organization pid
 * @param {string} params.orgType - Organization type (e.g. DISTRICT, SCHOOL)
 * @param {string} params.countryCode - ISO country code for the organization
 * @param {string} params.email - Requesting user's email address
 * @returns {Promise<Object>} Parsed JSON response containing the registration token
 * @throws {Error} When app config is missing or the request fails
 */
export const requestEarlyAccessRegistrationToken = async ({
  orgRefId,
  pid,
  orgType,
  countryCode,
  email,
}) => {
  const { oktaEarlyAccessRegistrationTokenApi } = getIdentifiedPlatformConfig();

  if (!oktaEarlyAccessRegistrationTokenApi) {
    throw new Error('Missing app config: oktaEarlyAccessRegistrationTokenApi');
  }

  const response = await fetch(oktaEarlyAccessRegistrationTokenApi, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify({ orgRefId, pid, orgType, countryCode, email }),
  });

  if (!response.ok) {
    const body = await Promise.resolve()
      .then(() => response.json())
      .catch(() => null);
    const error = new Error(
      `Failed to request early-access registration token: ${response.status} ${response.statusText}`,
    );
    error.serverError = body?.error;
    throw error;
  }

  return response.json();
};

export default requestEarlyAccessRegistrationToken;
